import { MoreHorizontal, Phone, Plus, Settings2, Trash2 } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader } from "@/components/layout/page";
import { SpeedDialFormDialog } from "@/features/speed-dial-form";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDeleteSpeedDial, useSpeedDials, useTenants } from "@/lib/queries";
import { atLeast, type SpeedDial } from "@/lib/types";
import { formatPhoneNumber } from "@/lib/utils";

/**
 * Speed dials (ADR-065): the outside numbers reps bring into a call by name, from Add participant.
 * Supervisors read the list; admins add, edit and remove. A superadmin reads every tenant's, so
 * for them the tenant is a column.
 */
export function SpeedDialsPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const canManage = atLeast(actor?.role, "admin");
  const isSuperadmin = actor?.role === "superadmin";
  const { data, isPending, error, refetch } = useSpeedDials();
  const { data: tenants } = useTenants({ enabled: isSuperadmin });
  const remove = useDeleteSpeedDial();

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<SpeedDial | undefined>();
  const [removing, setRemoving] = React.useState<SpeedDial | undefined>();

  const openForm = (dial?: SpeedDial) => {
    setEditing(dial);
    setFormOpen(true);
  };

  const confirmRemove = async () => {
    if (!removing) return;
    try {
      await remove.mutateAsync(removing.id);
      toast.success("Speed dial removed", removing.label);
      setRemoving(undefined);
    } catch (e) {
      toast.error(
        "Could not remove the speed dial",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    }
  };

  const newButton = canManage ? (
    <Button variant="default" onClick={() => openForm()}>
      <Plus />
      New speed dial
    </Button>
  ) : undefined;

  return (
    <Page>
      <PageHeader
        title="Speed dials"
        description="The outside numbers a rep can bring into a call by name, from Add participant on the phone. The person is rung from the call; the caller goes on hold while the rep talks to them first."
        action={newButton}
      />
      <Card className="overflow-hidden">
        {isPending ? (
          <TableSkeleton cols={4} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load speed dials"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Phone}
            title="No speed dials yet"
            description="Add the people reps bring into calls: an attorney, an interpreter, a partner firm. Until one exists, Add participant offers only a number box."
            action={newButton}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Number</TableHead>
                <TableHead>Order</TableHead>
                {isSuperadmin && <TableHead>Tenant</TableHead>}
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.id} className="[&>td]:h-11">
                  <TableCell className="font-medium text-text">{d.label}</TableCell>
                  <MonoCell>
                    <span className="inline-flex items-center gap-1">
                      {formatPhoneNumber(d.number)}
                      <CopyButton value={d.number} label="Copy number" />
                    </span>
                  </MonoCell>
                  <TableCell className="text-text-muted">{d.sort_order}</TableCell>
                  {isSuperadmin && (
                    <TableCell className="text-xs text-text-muted">
                      {tenants?.find((t) => t.id === d.tenant_id)?.name ?? "—"}
                    </TableCell>
                  )}
                  <TableCell>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${d.label}`}>
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onSelect={() => openForm(d)}>
                            <Settings2 />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => setRemoving(d)}>
                            <Trash2 />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <SpeedDialFormDialog open={formOpen} onOpenChange={setFormOpen} speedDial={editing} />

      <ConfirmDialog
        open={Boolean(removing)}
        onOpenChange={(open) => !open && setRemoving(undefined)}
        title="Remove this speed dial?"
        description={
          <>
            <span className="font-medium">{removing?.label}</span> disappears from Add participant.
            Calls already under way are not affected.
          </>
        }
        confirmLabel="Remove"
        loading={remove.isPending}
        onConfirm={() => void confirmRemove()}
      />
    </Page>
  );
}
